import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import mapboxgl, { AnyLayer } from 'mapbox-gl';
import cloneDeep from 'lodash/cloneDeep';
import { DataById } from '@redux/map/types';
import { usePresetsContext } from './presetsContext';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectLayersClickabilityById, selectSourcesById } from '@redux/selectors/selectors';
import {
    colorBehavior,
    getLayerColorForExpression,
    getSourceIdByLayerId,
    isDirectionsLayerById,
    isLabelsLayerById,
    lineBlurExpression,
    lineDashArrayExpression,
    lineWidthExpression,
    pointStrokeBehavior,
} from '@utils/mapUtils';

type ContextType = {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    enabledFilters: Record<string, any | any[]>;
    setEnabledFilters: (filters: Record<string, unknown>) => void;
    filters: any[];
    setFilters: (filters: DataById) => void;
    resetFilters: () => void;
    mapFilter: any[];
    setMapFilter: (arr: any[] | null) => void;
    layerPaint: Record<string, Record<string, unknown>>;
    highlightLayerFeatures: (args: {
        layerId: string;
        mapboxLayerType: AnyLayer['type'];
        featureStateIds?: string[];
    }) => void;
};

const MapFiltersContext = createContext<ContextType>({} as ContextType);

const useMapFiltersContext = () => {
    const ctx = useContext(MapFiltersContext);
    if (!ctx) {
        throw new Error('useMapFiltersContext must be used inside MapFiltersProvider');
    }
    return ctx;
};

type ProviderProps = {
    children: React.ReactNode;
    loadEnabledFilters?: (presetId: string) => Promise<{ filters; enabledFilters }>;
    defaultFilters?: Record<string, any | any[]>;
};

const MapFiltersProvider = ({ children, loadEnabledFilters, defaultFilters }: ProviderProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const { selectedPresetId } = usePresetsContext();
    const [enabledFilters, setEnabledFilters] = useState<any>(defaultFilters);
    const [defaultFiltersState, setDefaultFiltersState] = useState<any>([]);
    const [filters, setFilters] = useState<any>([]);
    const [mapFilter, setMapFilter] = useState<any>([]);
    const [layerPaint, setLayerPaint] = useState<Record<string, Record<string, unknown>>>({});
    const layersClickabilityById = useTypedSelector(selectLayersClickabilityById);
    const sourcesById = useTypedSelector(selectSourcesById);

    useEffect(() => {
        if (selectedPresetId && loadEnabledFilters) {
            void (async function () {
                setLoading(true);
                const { enabledFilters, filters } = await loadEnabledFilters(selectedPresetId);
                setEnabledFilters(enabledFilters);
                setFilters(filters);
                setDefaultFiltersState(cloneDeep(filters));
                setLoading(false);
            })();
        }
    }, [selectedPresetId, loadEnabledFilters]);

    const resetFilters = useCallback(() => {
        setFilters(defaultFiltersState);
    }, [defaultFiltersState]);

    const highlightLayerFeatures = useCallback(
        ({
            layerId,
            mapboxLayerType,
            featureStateIds = [],
        }: {
            layerId: string;
            mapboxLayerType: AnyLayer['type'];
            featureStateIds?: string[];
        }) => {
            const expression: mapboxgl.Expression = [
                'in',
                ['get', 'featureStateId'],
                ['literal', featureStateIds],
            ];

            if (layerId) {
                const source = sourcesById[getSourceIdByLayerId(layerId)];
                const { opacity } = source;

                // setLayerPaint(prevState => ({
                //     ...prevState,
                //     ...(mapboxLayerType === 'line'
                //         ? {
                //               [layerId]: {
                //                   'line-width': featureStateIds.length
                //                       ? ['case', expression, 5, 1]
                //                       : lineWidthExpression,
                //                   'line-opacity': featureStateIds.length
                //                       ? ['case', expression, 1, 0.7]
                //                       : opacity,
                //                   'line-blur': lineBlurExpression,
                //                   'line-dasharray': lineDashArrayExpression,
                //                   ...getLayerColorForExpression({
                //                       layerProperty: 'line-color',
                //                       expression,
                //                       isClickableLayer: layersClickabilityById[layerId],
                //                       areFeaturesSelected: !!featureStateIds.length,
                //                       highLightColor: colorBehavior,
                //                       regularColor: colorBehavior,
                //                       lockedColor: colorBehavior,
                //                   }),
                //               },
                //           }
                //         : {}),
                //     ...(mapboxLayerType === 'circle'
                //         ? {
                //               [layerId]: {
                //                   'circle-opacity': featureStateIds.length
                //                       ? ['case', expression, 1, 0.5]
                //                       : opacity,
                //                   ...getLayerColorForExpression({
                //                       layerProperty: 'circle-color',
                //                       expression,
                //                       isClickableLayer: layersClickabilityById[layerId],
                //                       areFeaturesSelected: !!featureStateIds.length,
                //                       highLightColor: colorBehavior,
                //                       regularColor: 'grey',
                //                       lockedColor: 'grey',
                //                   }),
                //                   'circle-radius': lineWidthExpression,
                //                   'circle-stroke-width': 1,
                //                   'circle-stroke-color': pointStrokeBehavior,
                //               },
                //           }
                //         : {}),
                //     ...(mapboxLayerType === 'fill'
                //         ? {
                //               [layerId]: {
                //                   'fill-opacity': featureStateIds.length
                //                       ? ['case', expression, 1, 0.5]
                //                       : opacity,
                //                   ...getLayerColorForExpression({
                //                       layerProperty: 'fill-color',
                //                       expression,
                //                       isClickableLayer: layersClickabilityById[layerId],
                //                       areFeaturesSelected: !!featureStateIds.length,
                //                       highLightColor: colorBehavior,
                //                       regularColor: 'grey',
                //                       lockedColor: 'grey',
                //                   }),
                //               },
                //           }
                //         : {}),
                //     ...(mapboxLayerType === 'symbol'
                //         ? {
                //               [layerId]: {
                //                   ...(isLabelsLayerById(layerId)
                //                       ? getLayerColorForExpression({
                //                             layerProperty: 'text-color',
                //                             expression,
                //                             isClickableLayer: layersClickabilityById[layerId],
                //                             areFeaturesSelected: !!featureStateIds.length,
                //                             highLightColor: colorBehavior,
                //                             regularColor: '#fff',
                //                             lockedColor: 'grey',
                //                         })
                //                       : {}),
                //                   ...(isDirectionsLayerById(layerId)
                //                       ? getLayerColorForExpression({
                //                             layerProperty: 'text-color',
                //                             expression,
                //                             isClickableLayer: layersClickabilityById[layerId],
                //                             areFeaturesSelected: !!featureStateIds.length,
                //                             highLightColor: '#fff',
                //                             regularColor: '#fff',
                //                             lockedColor: '#fff',
                //                         })
                //                       : {}),
                //               },
                //           }
                //         : {}),
                // }));
            }
        },
        [layersClickabilityById, sourcesById]
    );

    const value = useMemo(
        () => ({
            loading,
            setLoading,
            enabledFilters,
            setEnabledFilters,
            filters,
            resetFilters,
            setFilters,
            mapFilter,
            setMapFilter,
            layerPaint,
            highlightLayerFeatures,
        }),
        [
            loading,
            setLoading,
            enabledFilters,
            setEnabledFilters,
            filters,
            resetFilters,
            setFilters,
            mapFilter,
            setMapFilter,
            layerPaint,
            highlightLayerFeatures,
        ]
    );

    return <MapFiltersContext.Provider value={value}>{children}</MapFiltersContext.Provider>;
};

export { MapFiltersProvider, useMapFiltersContext };
